import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Intent } from '@blueprintjs/core';
import { FormSwitch } from 'components/form-fields';
import { NotificationToaster, NotificationInline, AlertUnsavedData, AlertConfirm } from 'components/notifications';
import { ButtonSave } from 'components/buttons';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { ShowHide } from 'components/layout';
import {
    UserLicenceCheckingPermissions,
    licenceCheckingRequiredAction,
    noLicenceCheckingAccess,
    hasLicenceCheckingPermissionChanges
} from './UserLicenceCheckingPermissions';
import { UserService, LicenceCheckingService } from 'services';
import { useDirtyData } from 'hooks/useDirtyData';
import { HelperFunctions } from 'helpers';

const requiredAction = "Users:Manage";
const badRequestCode = 400;
const permissionsLoadFailedMessage = "Could not load settings. Please refresh the page.";
const permissionsLoadFailedInlineMessage = "Additional permissions are currently unavailable. Refresh the page to try again.";
const licenceCheckingLoadFailedMessage = "Could not load licence checking access. Please refresh the page.";
const licenceCheckingSaveFailedMessage = "Failed to update licence checking access";
const licenceCheckingRemoveFailedMessage = "Failed to remove licence checking access";
const savedMessage = "Settings updated successfully.";

const noSensitiveInformationAccess = { canAccessSensitiveEmployeeInformation: false };

// The licence checking slice is saved through three different endpoints, so the pending change is
// classified once and reused by both the dirty check and the save handler.
const licenceCheckingChanges = {
    none: "none",
    add: "add",
    edit: "edit",
    remove: "remove"
};

// The reused LicenceCheckingService write methods reject with the raw axios error, so a 400's
// validation message (e.g. removing a primary contact) is surfaced rather than a dead-end generic.
// A rejection can arrive without a body, which cannot be shown, so the fallback is used instead.
function getFailureMessage(error, fallbackMessage) {
    const apiMessage = typeof error?.response?.data === "string" ? error.response.data.trim() : "";

    if (error?.response?.status === badRequestCode && apiMessage.length > 0) {
        return apiMessage;
    }

    return fallbackMessage;
}

export function UserAdditionalPermissions(
    {
        user = null
    }
) {
    const [saving, setSaving] = useState(false);
    const [removeConfirmVisible, setRemoveConfirmVisible] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [permissionsLoadFailed, setPermissionsLoadFailed] = useState(false);
    const [permissions, setPermissions] = useState(noSensitiveInformationAccess);
    const [originalValues, setOriginalValues] = useState(noSensitiveInformationAccess);
    const [licenceCheckingLoading, setLicenceCheckingLoading] = useState(true);
    const [licenceCheckingLoadFailed, setLicenceCheckingLoadFailed] = useState(false);
    const [isLicenceCheckingUser, setIsLicenceCheckingUser] = useState(false);
    const [wasLicenceCheckingUser, setWasLicenceCheckingUser] = useState(false);
    const [licenceCheckingPermissions, setLicenceCheckingPermissions] = useState(noLicenceCheckingAccess);
    const [licenceCheckingOriginalValues, setLicenceCheckingOriginalValues] = useState(noLicenceCheckingAccess);

    const canManageUsers = UserHasAccess(requiredAction);
    const canManageLicenceChecking = UserHasAccess(licenceCheckingRequiredAction);
    const loggedInUserId = useSelector(state => state.loggedInUser.userId);

    // The user id can arrive as a string and the logged in user id is a number, so both are coerced.
    const isOwnRecord = user != null && user.userId != null && Number(user.userId) === Number(loggedInUserId);

    // Additional permissions are not applicable to driver users, and the API rejects them, so the
    // nested route is refused here as well as being hidden from the user's tab list.
    const isDriverUser = user != null && user.driverId != null;

    const showLicenceChecking = canManageLicenceChecking && !isOwnRecord && !isDriverUser;
    const canSavePermissions = !permissionsLoadFailed;
    const canSaveLicenceChecking = showLicenceChecking && !licenceCheckingLoadFailed;
    const showSave = !isOwnRecord && (canSavePermissions || canSaveLicenceChecking);
    const loading = permissionsLoading || licenceCheckingLoading;

    const permissionsChanged = canSavePermissions
        && permissions.canAccessSensitiveEmployeeInformation !== originalValues.canAccessSensitiveEmployeeInformation;
    const licenceCheckingChange = getLicenceCheckingChange();

    // A single useDirtyData instance drives both the router blocker and the global dirtyData flag
    // (which guards full-page redirects like sign-out), so both slices are folded into the compared
    // objects rather than tracked by a second hook instance. The compared values are the pending
    // change of each slice, which keeps the original side constant and lets the hook's
    // object-identity effects fire whenever either slice changes or is saved.
    const dirtyOriginalValues = useMemo(
        () => ({ permissionsChanged: false, licenceCheckingChange: licenceCheckingChanges.none }),
        []);
    const dirtyCurrentValues = useMemo(
        () => ({ permissionsChanged, licenceCheckingChange }),
        [permissionsChanged, licenceCheckingChange]);
    const isDataDirty = useDirtyData(dirtyOriginalValues, dirtyCurrentValues);

    useEffect(loadPermissions, [user, canManageUsers, isDriverUser]);
    useEffect(loadLicenceCheckingAccess, [user, showLicenceChecking]);

    function loadPermissions() {

        if (!canManageUsers || isDriverUser || user == null || user.userId == null) {
            return;
        }

        UserService.getAdditionalPermissions(user.userId).then(
            (additionalPermissions) => {
                const loadedPermissions = {
                    canAccessSensitiveEmployeeInformation: additionalPermissions.canAccessSensitiveEmployeeInformation === true
                };

                setPermissions(loadedPermissions);
                setOriginalValues(HelperFunctions.deepClone(loadedPermissions));
                setPermissionsLoading(false);
            },
            () => {
                setPermissionsLoading(false);
                setPermissionsLoadFailed(true);
                NotificationToaster.show(Intent.DANGER, permissionsLoadFailedMessage);
            });
    }

    function loadLicenceCheckingAccess() {

        // The section will never render for this caller, so the shared save button must not be held
        // in its loading state waiting for a request that is never made.
        if (!showLicenceChecking) {
            setLicenceCheckingLoading(false);
            return;
        }

        if (user == null || user.userId == null) {
            return;
        }

        LicenceCheckingService.getLicenceCheckingUser(user.userId).then(
            (licenceCheckingUser) => {
                setLicenceCheckingLoading(false);

                // The response reports whether the record is a licence checking user; when they are
                // not, the flag starts off and turning it on creates the access rather than updating it.
                if (!licenceCheckingUser.isLicenceCheckingUser) {
                    setIsLicenceCheckingUser(false);
                    setWasLicenceCheckingUser(false);
                    setLicenceCheckingPermissions({ ...noLicenceCheckingAccess });
                    setLicenceCheckingOriginalValues({ ...noLicenceCheckingAccess });
                    return;
                }

                const loadedPermissions = {
                    allowPurchaseCredits: licenceCheckingUser.permissions?.allowPurchaseCredits === true,
                    performManualChecks: licenceCheckingUser.permissions?.performManualChecks === true,
                    allowAccessGreyFleet: licenceCheckingUser.permissions?.allowAccessGreyFleet === true
                };

                setIsLicenceCheckingUser(true);
                setWasLicenceCheckingUser(true);
                setLicenceCheckingPermissions(loadedPermissions);
                setLicenceCheckingOriginalValues({ ...loadedPermissions });
            },
            () => {
                setLicenceCheckingLoading(false);
                setLicenceCheckingLoadFailed(true);
                NotificationToaster.show(Intent.DANGER, licenceCheckingLoadFailedMessage);
            });
    }

    function getLicenceCheckingChange() {

        if (!canSaveLicenceChecking) {
            return licenceCheckingChanges.none;
        }

        if (isLicenceCheckingUser !== wasLicenceCheckingUser) {
            return isLicenceCheckingUser ? licenceCheckingChanges.add : licenceCheckingChanges.remove;
        }

        // Individual permissions are only editable, and only comparable, while the user is flagged
        // as a licence checking user.
        if (isLicenceCheckingUser && hasLicenceCheckingPermissionChanges(licenceCheckingPermissions, licenceCheckingOriginalValues)) {
            return licenceCheckingChanges.edit;
        }

        return licenceCheckingChanges.none;
    }

    function onCanAccessSensitiveEmployeeInformationChange(event) {
        setPermissions({
            ...permissions,
            canAccessSensitiveEmployeeInformation: event.target.checked
        });
    }

    function onIsLicenceCheckingUserChange(event) {
        setIsLicenceCheckingUser(event.target.checked);
    }

    function onLicenceCheckingPermissionChange(field, event) {
        const checked = event.target.checked;

        setLicenceCheckingPermissions((previousPermissions) => {
            return { ...previousPermissions, [field]: checked };
        });
    }

    function savePermissions() {
        const savedPermissions = { ...permissions };

        return UserService.saveAdditionalPermissions(user.userId, savedPermissions).then(
            () => {
                setOriginalValues(HelperFunctions.deepClone(savedPermissions));
                return true;
            },
            (error) => {
                let intent = Intent.DANGER;

                if (error.status === badRequestCode) {
                    intent = Intent.WARNING;
                }

                NotificationToaster.show(intent, error.data);
                return false;
            });
    }

    function removeLicenceCheckingAccess() {
        return LicenceCheckingService.deleteLicenceCheckingUser(user.userId).then(
            () => {
                setWasLicenceCheckingUser(false);
                setLicenceCheckingPermissions({ ...noLicenceCheckingAccess });
                setLicenceCheckingOriginalValues({ ...noLicenceCheckingAccess });
                return true;
            },
            (error) => {
                NotificationToaster.show(Intent.DANGER, getFailureMessage(error, licenceCheckingRemoveFailedMessage));
                return false;
            });
    }

    function saveLicenceCheckingAccess() {

        if (licenceCheckingChange === licenceCheckingChanges.remove) {
            return removeLicenceCheckingAccess();
        }

        const savedPermissions = { ...licenceCheckingPermissions };

        // The read model names the manual checking permission differently to the write model.
        const requestPermissions = {
            allowPurchaseCredits: savedPermissions.allowPurchaseCredits,
            allowManualChecking: savedPermissions.performManualChecks,
            allowAccessGreyFleet: savedPermissions.allowAccessGreyFleet
        };

        let request;

        if (licenceCheckingChange === licenceCheckingChanges.add) {
            request = LicenceCheckingService.addUser({ userId: user.userId, ...requestPermissions });
        } else {
            request = LicenceCheckingService.editLicenceCheckingUser(user.userId, requestPermissions);
        }

        return request.then(
            () => {
                setWasLicenceCheckingUser(true);
                setLicenceCheckingOriginalValues({ ...savedPermissions });
                return true;
            },
            (error) => {
                NotificationToaster.show(Intent.DANGER, getFailureMessage(error, licenceCheckingSaveFailedMessage));
                return false;
            });
    }

    // Each slice reports its own outcome so a failure leaves that slice unsaved and retryable while
    // the other slice still commits, and the success message is only shown when nothing failed.
    function saveChanges() {
        const requests = [];

        if (permissionsChanged) {
            requests.push(savePermissions());
        }

        if (licenceCheckingChange !== licenceCheckingChanges.none) {
            requests.push(saveLicenceCheckingAccess());
        }

        if (requests.length === 0) {
            return Promise.resolve();
        }

        setSaving(true);

        return Promise.all(requests).then((results) => {
            setSaving(false);

            if (results.every((sliceSaved) => sliceSaved)) {
                NotificationToaster.show(Intent.SUCCESS, savedMessage);
            }
        });
    }

    // Removing licence checking access must stay deliberate, so the whole save waits behind the
    // confirmation and cancelling leaves every slice unsaved.
    function onSaveClick() {

        if (licenceCheckingChange === licenceCheckingChanges.remove) {
            setRemoveConfirmVisible(true);
            return;
        }

        saveChanges();
    }

    function onRemoveConfirm() {
        saveChanges().then(() => {
            setRemoveConfirmVisible(false);
        });
    }

    function onRemoveCancel() {
        setRemoveConfirmVisible(false);
    }

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={isDriverUser}
                    show={(
                        <PageRestricted />
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <h2>Additional Permissions</h2>
                                <ShowHide
                                    evaluator={permissionsLoadFailed}
                                    show={(
                                        <NotificationInline
                                            allowClose={false}
                                            show
                                            text={permissionsLoadFailedInlineMessage}
                                            intent="danger"
                                        />
                                    )}
                                    hide={(
                                        <FormSwitch
                                            id="can-access-sensitive-employee-information"
                                            headingText="Manage sensitive employee information:"
                                            helperText="Allows the user to manage sensitive employee information, such as medical data."
                                            checked={permissions.canAccessSensitiveEmployeeInformation}
                                            loading={loading}
                                            disabled={saving || loading || isOwnRecord}
                                            onChange={onCanAccessSensitiveEmployeeInformationChange}
                                        ></FormSwitch>
                                    )}
                                />
                            </div>

                            <ShowHide
                                evaluator={showLicenceChecking}
                                show={(
                                    <UserLicenceCheckingPermissions
                                        isLicenceCheckingUser={isLicenceCheckingUser}
                                        permissions={licenceCheckingPermissions}
                                        loading={loading}
                                        disabled={saving || loading}
                                        loadFailed={licenceCheckingLoadFailed}
                                        onIsLicenceCheckingUserChange={onIsLicenceCheckingUserChange}
                                        onPermissionChange={onLicenceCheckingPermissionChange}
                                    />
                                )}
                            />

                            <ShowHide
                                evaluator={showSave}
                                show={(
                                    <div className="row">
                                        <ButtonSave
                                            id="save-additional-permissions"
                                            onClick={onSaveClick}
                                            disabled={saving || loading}
                                            loading={loading}
                                        ></ButtonSave>
                                    </div>
                                )}
                            />

                            <AlertConfirm
                                isOpen={removeConfirmVisible}
                                title="Remove Licence Checking Access"
                                onConfirm={onRemoveConfirm}
                                onCancel={onRemoveCancel}
                                confirmButtonText="Remove"
                                cancelButtonText="Cancel"
                                saving={saving}
                            >
                                <p>Are you sure you want to remove this user&apos;s licence checking access?</p>
                            </AlertConfirm>

                            <AlertUnsavedData
                                isDirty={isDataDirty}>
                            </AlertUnsavedData>
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

UserAdditionalPermissions.propTypes = {
    user: PropTypes.shape({
        userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        driverId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
};
